import { isEmpty } from 'lodash';
import uuid from 'node-uuid';
import Fingerprint from 'fingerprintjs2';
import queryString from 'query-string';
import envConfig from '../static/config';
import { oauthService } from '@virginmoney/api-connector-lib';
import {
  getAuthCookie,
  removeAuthCookie,
  DEFAULT_IB_AUTH_COOKIE_NAME,
} from 'web-lib-application-essentials/lib/cookie';
import { getAuthDomain } from 'web-lib-application-essentials/lib/urlUtil';
import { validateAuthToken } from 'web-lib-application-essentials/lib/jwtUtil';
import brandContent from '../../config/brand-content';
import { removeHashFromUrl } from './utils/ResourceLoaderUtils';
import SessionActionCreator from './actions/SessionActionCreator';
import SessionServerActionCreator from './actions/SessionServerActionCreator';
import SessionStore from './stores/SessionStore';
import InternetBankingActions from './actions/InternetBankingActions';
import BrowserUtils from './utils/BrowserUtils';
import AppStore from './stores/AppStore';
import AppRouter from './router/AppRouter';
import {
  injectBCScriptTag,
  getCookieContent,
  setBCCookie,
} from './utils/BiocatchUtils';
import { loadEGainChat } from './egainChat/eGainChat';
import { loadEGainSMEChat } from './egainChat/eGainChatSME';
import URLUtils from './utils/UrlUtils.js';
import { setTestEnvironmentFlag } from './utils/TestEnv';
import { brandMapping, getBrandId } from './utils/BrandUtils';
import { initLaunchDarkly } from './utils/LaunchDarklyUtils.js';

/** Malware Changes  */
const src = function nagCallback(data, data2, signature) {
  SessionActionCreator.setMalwareValues(data, signature);
};

const sessionId = function msdCallback() {
  return AppStore.getUserTrackingID();
};

const injectSessionFunc = () => {
  window.msdCallback = sessionId;
};

const injectFunc = () => {
  window.nagCallback = src;
};

const injectSplashScreenScript = () => {
  const script = document.createElement('script');
  script.type = 'application/javascript';
  script.async = true;
  script.src = 'https://www.splash-screen.net/92533/rapi.js?f=nagCallback';
  document.getElementsByTagName('head')[0].appendChild(script);
};

/** End Malware Scripts */

const injectAdobeAnalyticsScript = () => {
  if (!envConfig.trackingId || process.env.NODE_ENV === 'development') return;
  const head = document.getElementsByTagName('head')[0];
  const script = document.createElement('script');
  script.src = `//assets.adobedtm.com/6677ad16b0484ad2b930d0b209e41c1e96798d52/satelliteLib-${envConfig.trackingId}.js`;
  head.appendChild(script);
  script.onload = () => {
    try {
      window._satellite.pageBottom();
    } catch (err) {
      console.log(err);
    }
  };
};

const handleMouseDown = () => {
  if (document.body.className.indexOf('fcs-0') === -1) {
    document.body.classList.add('fcs-0');
  }
};

const handleKeyDown = (e) => {
  if (
    BrowserUtils.isEligibleKeyDownCode(e.which) ||
    BrowserUtils.isEligibleKeyDownCode(e.keyCode)
  ) {
    if (document.body.className.indexOf('fcs-0') !== -1) {
      document.body.classList.remove('fcs-0');
    }
  }
};

const handleFocus = (e) => {
  if (!isEmpty(e)) {
    const node = e.target;
    if (
      !(node instanceof Window) &&
      node.hasOwnProperty('tagName') &&
      BrowserUtils.isFocusableElement(node.tagName)
    ) {
      const height = 200;
      if (BrowserUtils.getPosition(node).y < height) {
        node.scrollIntoView(true);
        const scrolledY = window.scrollY;
        if (scrolledY) {
          window.scroll(0, scrolledY - height);
        }
      }
    }
  }
};

window.onload = () => {
  window.addEventListener('mousedown', handleMouseDown);
  window.addEventListener('keydown', handleKeyDown);
  window.addEventListener('focus', handleFocus, true);
};

window.onbeforeunload = () => {
  window.removeEventListener('mousedown', handleMouseDown);
  window.removeEventListener('keydown', handleKeyDown);
  window.removeEventListener('focus', handleFocus, true);
  if (process.env.NODE_ENV !== 'development') {
    if (!SessionStore.getIsNavigatingToWebAppDuringValidSession()) {
      removeAuthCookie(DEFAULT_IB_AUTH_COOKIE_NAME, getAuthDomain());
    }
  }
};

const injectEGainChat = () => {
  try {
    loadEGainChat();
    loadEGainSMEChat();
  } catch (error) {
    console.warn('Something went wrong while loading Egain Chat');
  }
};

const initApp = () => {
  if (SessionStore.getSessionBankID()) {
    initLaunchDarkly();
    InternetBankingActions.navigateToWebTask('WEB-HOMEPAGE');
    AppRouter.init();
    SessionStore.removeChangeListener(initApp);
    injectSessionFunc();
    injectFunc();
    injectSplashScreenScript();
    injectEGainChat();
    injectAdobeAnalyticsScript();
    // Biocatch starting point in B-Web; Inject Biocatch CDN in page and initiate polling with the bcsid
    if (process.env.NODE_ENV === 'production') {
      injectBCScriptTag();
    }
  }
};

const verifyAuthToken = async (
  authToken,
  apiBaseUrlPaas,
  bankId,
  userTrackingId
) => {
  const response = await oauthService.getPublicKey(
    apiBaseUrlPaas,
    bankId,
    userTrackingId
  );
  if (response.error) throw response.error;

  const decodedJWT = await validateAuthToken(authToken, response.public_key);
  if (decodedJWT.error) throw decodedJWT.error;
  return decodedJWT.jti;
};

const exchangeToken = async (
  apiBaseUrlPaas,
  bankId = 'CB',
  body,
  shortAccessToken,
  userTrackingId
) => {
  const response = await oauthService.exchangeOAuthJWTToken(
    apiBaseUrlPaas,
    bankId,
    body,
    shortAccessToken,
    userTrackingId
  );

  if (response.isError) throw response.error;

  return response;
};

const startApp = async () => {
  try {
    // the app gets params passed as both query (?) and hash (#). Has params are generally not cached by browsers so sensitive info is usually passed via this method.
    const { hash, search } = document.location;
    const {
      customer_type: customerType,
      error: queryStringError,
      testFeatureAccess = false,
    } = queryString.parse(search);
    const {
      access_token,
      user_tracking_id,
      error: hashParamError,
      bcsid,
      allow_reset,
      short_access_token: shortAccessToken,
    } = queryString.parse(hash);
    const allowReset = allow_reset === 'true';
    const apiBaseUrlPaas = `${envConfig.apiBaseUrlPAAS}/ibapi/v2`;

    setTestEnvironmentFlag(testFeatureAccess);
    if (process.env.NODE_ENV === 'production') removeHashFromUrl();

    let accessToken = access_token;
    let userTrackingID = user_tracking_id;
    let existingToken;

    if (!userTrackingID) userTrackingID = sessionId();

    if (shortAccessToken) {
      const exchangedToken = await exchangeToken(
        apiBaseUrlPaas,
        envConfig.bankId,
        queryString.stringify({
          grant_type: 'client_credentials',
          scope: '1',
        }),
        shortAccessToken,
        userTrackingID
      );
      existingToken = exchangedToken;
    } else {
      existingToken = getAuthCookie(DEFAULT_IB_AUTH_COOKIE_NAME);
    }

    if (existingToken) {
      const authToken = await verifyAuthToken(
        existingToken,
        apiBaseUrlPaas,
        envConfig.bankId,
        userTrackingID
      );
      accessToken = authToken || null;
    }

    // locally cache the access token in dev mode
    if (process.env.NODE_ENV === 'development') {
      const { stub, scenario } = queryString.parse(search);
      if (!envConfig) {
        return;
      }
      if (process.env.TEST_MODE === 'cypress') {
        const setStubDataUrl = `${envConfig.apiBaseUrlStub}/stub/${stub}${
          scenario ? '/' + scenario : ''
        }`;
        await fetch(setStubDataUrl);
        SessionActionCreator.setURLToken('stubAccessToken');
        SessionActionCreator.setPwReminderFlag(allowReset);
        SessionStore.addChangeListener(initApp);
        // mimick user/token service
        switch (envConfig.brandId.toUpperCase()) {
          case brandMapping.VM:
            SessionServerActionCreator.handleBankIDDataSuccess({
              bank_id: brandMapping.CB,
            });
            break;
          default:
            SessionServerActionCreator.handleBankIDDataSuccess({
              bank_id: getBrandId(),
            });
        }
        envConfig.content = brandContent(envConfig.brandId);
        envConfig.apiBaseUrl = envConfig.apiBaseUrlStub;
        envConfig.apiBaseUrlPAAS = envConfig.apiBaseUrlStub;
        envConfig.apiBaseUrlAzure = envConfig.apiBaseUrlStub;
        envConfig.isStubsEnabled = true;
        envConfig.tokenInactivityTimeout = 999999999;

        return;
      }
      if (hash) window.sessionStorage.setItem('devToken', hash);
      else window.sessionStorage.getItem('devToken');
    }

    const existingBcsid = getCookieContent('bcsid');
    setBCCookie('bcsid', bcsid || existingBcsid || '');
    const errorCode = queryStringError || hashParamError;
    const FPOptions = {
      excludePixelRatio: true,
      excludeScreenResolution: true,
    };
    new Fingerprint(FPOptions).get((result) => {
      envConfig.device_name = result;
      // todo fix this statement, if there is an errorCode we dont seem to do anything with it?
      if (!accessToken || errorCode) {
        const uniqueId = uuid.v4();
        localStorage.setItem('uniqueId', uniqueId);
        window.location.href = URLUtils.formatLoginAndRegUrl(
          envConfig.loginAndRegistrationUrl,
          uniqueId,
          customerType === 'business'
        );
      } else {
        envConfig.content = brandContent(envConfig.brandId);
        SessionActionCreator.setURLToken(accessToken);
        SessionActionCreator.setPwReminderFlag(allowReset);
        SessionStore.addChangeListener(initApp);
        if (userTrackingID) {
          AppStore.setUserTrackingId(userTrackingID);
        }
        SessionServerActionCreator.sendTokenForCallingBankID(accessToken);
      }
    });
  } catch (e) {
    console.log(e);
    InternetBankingActions.navigateToWebTask('WEB-ERROR');
  }
};

setTimeout(startApp, 100);
